/**
 * Buscador del menú lateral (round 2026-10-28).
 *
 * Funciones puras: no saben de React ni de roles. El SideBar les pasa SOLO los ítems que el rol
 * ya ve en el menú (la misma lista filtrada que dibuja), así que el buscador nunca puede mostrar
 * algo que el rol no debe ver.
 *
 * Reglas de coincidencia:
 *  - Sin distinguir mayúsculas ni tildes («lavanderia» encuentra «Lavandería»; «ñ» ≈ «n»).
 *  - La consulta se parte en palabras y TODAS deben coincidir, en cualquier orden y por partes de
 *    palabra («finanz emp» encuentra «Finanzas empresa»).
 *  - Cada palabra se busca en el título, en los sinónimos del ítem (`keywords`) y en el nombre de
 *    su grupo (OPERACIONES, GESTIÓN…).
 *  - Orden: primero lo que coincide en el título (mejor si es al comienzo de una palabra), después
 *    sinónimos y grupo; a igual puntaje se conserva el orden del menú.
 */

export interface ItemBuscable {
  title: string;
  /** Sinónimos o palabras relacionadas («backup» ↔ «respaldo»). Opcional. */
  keywords?: readonly string[];
  /** Nombre del grupo o sección del menú. */
  group: string;
}

const MARCAS_COMBINANTES = /[̀-ͯ]/g;

/** Normaliza un texto y devuelve, por cada carácter normalizado, el índice del original. */
export function normalizarConMapa(texto: string): { texto: string; mapa: number[] } {
  let salida = "";
  const mapa: number[] = [];
  const original = String(texto ?? "");
  for (let i = 0; i < original.length; i++) {
    // Un par sustituto (emoji) se normaliza junto; basta con tratarlo carácter a carácter.
    const n = original[i].normalize("NFD").replace(MARCAS_COMBINANTES, "").toLowerCase();
    for (let j = 0; j < n.length; j++) {
      salida += n[j];
      mapa.push(i);
    }
  }
  return { texto: salida, mapa };
}

/** Texto en minúsculas y sin tildes (para comparar). */
export const normalizar = (texto: string): string => normalizarConMapa(texto).texto;

/** Palabras de la consulta, normalizadas y sin duplicados. Vacío = sin búsqueda. */
export function tokensDeBusqueda(consulta: string): string[] {
  const partes = normalizar(consulta).split(/\s+/).filter(Boolean);
  return Array.from(new Set(partes));
}

const esLetraONumero = (c: string | undefined): boolean => !!c && /[a-z0-9]/.test(c);

/** Índice de la primera aparición de `token` al comienzo de una palabra, o -1. */
function indiceComienzoDePalabra(texto: string, token: string): number {
  let desde = 0;
  for (;;) {
    const i = texto.indexOf(token, desde);
    if (i === -1) return -1;
    if (i === 0 || !esLetraONumero(texto[i - 1])) return i;
    desde = i + 1;
  }
}

/** Puntaje de UNA palabra contra un ítem: 0 = no coincide. */
function puntajeDeToken(token: string, titulo: string, keywords: string[], grupo: string): number {
  if (indiceComienzoDePalabra(titulo, token) !== -1) return 5;
  if (titulo.includes(token)) return 4;
  if (keywords.some((k) => indiceComienzoDePalabra(k, token) !== -1)) return 3;
  if (keywords.some((k) => k.includes(token))) return 2;
  if (grupo.includes(token)) return 1;
  return 0;
}

/** Puntaje total del ítem (0 = no coincide: alguna palabra no aparece en ninguna parte). */
export function puntajeDeItem(item: ItemBuscable, tokens: string[]): number {
  if (tokens.length === 0) return 0;
  const titulo = normalizar(item.title);
  const keywords = (item.keywords ?? []).map(normalizar);
  const grupo = normalizar(item.group);
  let total = 0;
  for (const t of tokens) {
    const p = puntajeDeToken(t, titulo, keywords, grupo);
    if (p === 0) return 0;
    total += p;
  }
  return total;
}

/**
 * Filtra y ordena. Con la consulta vacía devuelve `[]` (el menú normal no se toca).
 * El orden es estable: a igual puntaje se conserva el orden de `items`.
 */
export function buscarEnMenu<T extends ItemBuscable>(items: readonly T[], consulta: string): T[] {
  const tokens = tokensDeBusqueda(consulta);
  if (tokens.length === 0) return [];
  return items
    .map((item, orden) => ({ item, orden, puntaje: puntajeDeItem(item, tokens) }))
    .filter((x) => x.puntaje > 0)
    .sort((a, b) => b.puntaje - a.puntaje || a.orden - b.orden)
    .map((x) => x.item);
}

/** Rangos `[inicio, fin)` del TÍTULO ORIGINAL donde calzan las palabras de la consulta. */
export function rangosDeResaltado(titulo: string, tokens: string[]): Array<[number, number]> {
  const { texto, mapa } = normalizarConMapa(titulo);
  const crudos: Array<[number, number]> = [];
  for (const t of tokens) {
    let i = indiceComienzoDePalabra(texto, t);
    if (i === -1) i = texto.indexOf(t);
    if (i === -1) continue;
    // El rango normalizado [i, i+len) vuelve al texto original por el mapa.
    crudos.push([mapa[i], mapa[i + t.length - 1] + 1]);
  }
  crudos.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const unidos: Array<[number, number]> = [];
  for (const r of crudos) {
    const ultimo = unidos[unidos.length - 1];
    if (ultimo && r[0] <= ultimo[1]) ultimo[1] = Math.max(ultimo[1], r[1]);
    else unidos.push([r[0], r[1]]);
  }
  return unidos;
}

/** Parte el título en segmentos `{ texto, resaltado }` para dibujarlo con `<mark>`. */
export function segmentosResaltados(
  titulo: string,
  tokens: string[],
): Array<{ texto: string; resaltado: boolean }> {
  const rangos = rangosDeResaltado(titulo, tokens);
  if (rangos.length === 0) return [{ texto: titulo, resaltado: false }];
  const segmentos: Array<{ texto: string; resaltado: boolean }> = [];
  let cursor = 0;
  for (const [ini, fin] of rangos) {
    if (ini > cursor) segmentos.push({ texto: titulo.slice(cursor, ini), resaltado: false });
    segmentos.push({ texto: titulo.slice(ini, fin), resaltado: true });
    cursor = fin;
  }
  if (cursor < titulo.length) segmentos.push({ texto: titulo.slice(cursor), resaltado: false });
  return segmentos;
}

/**
 * Si el ítem calzó por un sinónimo (no por el título), devuelve ese sinónimo para mostrarlo
 * («Backup BD · respaldo»): explica por qué apareció. `null` si el título ya lo explica.
 */
export function sinonimoCoincidente(item: ItemBuscable, tokens: string[]): string | null {
  const titulo = normalizar(item.title);
  const fuera = tokens.filter((t) => !titulo.includes(t));
  if (fuera.length === 0) return null;
  for (const k of item.keywords ?? []) {
    const nk = normalizar(k);
    if (fuera.some((t) => nk.includes(t))) return k;
  }
  return null;
}
